import { BILLING_ADDONS, PLAN_NAMES } from 'constants/billing';

const upgradeOrgSavedPromptsTitle = `Please upgrade to an ${PLAN_NAMES.organization} plan or above to create and view org-level saved prompts`;

export const L10nCopilot = {
  askAvoma: 'Ask Avoma',
  homeAskHeading: 'What would you like to accomplish?',
  homeAskCompactPlaceholder:
    'Ask Avoma about everything. Use ‘@’ to refer Meetings, Deals and Companies.',
  homeAskExpandAriaLabel: 'Expand Ask Avoma composer',
  homeAskVisibilityAriaLabel: 'Show Ask Avoma on this Home view',
  homeAskSuggestedPrompts: [
    'List down my to-dos',
    'Plan my week',
    'Summarize my last meeting',
    'Prep Design sync meeting'
  ],
  homeAskDisplayMode: 'Ask Avoma display mode',
  homeAskDisplayPopup: 'Pop up',
  homeAskDisplaySidebar: 'Side bar',
  homeAskDisplayFull: 'Full screen',
  homeAskDisplaySelected: 'selected',
  askAvomaForDeals: 'Ask Avoma for Deals',
  askAvomaForCompanies: 'Ask Avoma for Companies',
  globalAskAvoma: 'Global Ask Avoma',
  copilotDisabledTooltip:
    'Ask Avoma is not available for this conversation. Please contact support for assistance.',
  copilotDisabledMeetingNotReadyTooltip:
    'A transcript is required to use Ask Avoma for this conversation.',
  copilotDisabledSpeakerDiNotReadyTooltip:
    'Speaker identification must be completed to use Ask Avoma for this conversation. Please contact support for assistance.',
  suggestedPrompts: 'Suggested Prompts',
  showSuggestedPrompts: 'Show Suggested Prompts',
  hideSuggestedPrompts: 'Hide Suggested Prompts',
  copiedToClipboardToast: 'Message copied to clipboard',
  promptCopiedToClipboardToast: 'Prompt copied to clipboard',
  copyMessageTooltip: 'Copy message',
  copyResponseTooltip: 'Copy response',
  copyPromptTooltip: 'Copy prompt',
  addToNotesTooltip: 'Add response to notes',
  addToNotesDisabledTooltip:
    'Add to notes is not available for this conversation',
  resubmitMessageTooltip: 'Resubmit message',
  reconnect: 'Reconnect',
  reconnectTooltip: 'Reconnect to Avoma AI',
  avomaAiMemberName: 'Avoma AI',
  thumbsUpTooltip: 'Helpful',
  thumbsDownTooltip: 'Not helpful',
  feedbackTitle: 'Tell us more',
  additionalFeedbackPlaceholder: 'Provide additional feedback',
  sendFeedbackTooltip: 'Send feedback',
  feedbackDialogTitle: 'Send feedback',
  copilotFeedbackPlaceholder: 'Provide feedback about this conversation',
  tabMode: 'Tab mode',
  multitaskingMode: 'Multitasking mode',
  fullScreenMode: 'Full screen mode',
  switchToTabMode: 'Switch to tab mode',
  multitaskingModeInfo:
    'You are currently using Ask Avoma in multitasking mode',
  moreActions: 'More actions',
  feedbackSuccessToast: 'Feedback sent successfully',
  feedbackErrorToast: 'Failed to send feedback',
  sendMessageError: 'Failed to send message',
  sendMessageAriaLabel: 'Send message',
  stopResponseAriaLabel: 'Stop response',
  connectionLostErrorMessage:
    'Connection lost. Check your network and try sending your message again.',
  /** Inline error when the latest user message has no Ask Avoma reply yet. */
  copilotNoReplyStartedTimeoutMessage:
    "Ask Avoma couldn't respond. Try sending your message again.",
  /** Inline error when a reply from Ask Avoma did not finish. */
  copilotInProgressStallTimeoutMessage:
    "Ask Avoma couldn't complete that response. Try sending your message again.",
  /** Fallback when the UI shows an inline error but the reason is unknown or new. */
  copilotUnexpectedErrorMessage:
    'Something went wrong. Try sending your message again.',
  upgradeTitle: `Please upgrade to a ${PLAN_NAMES.team} plan or above to access Ask Avoma for meetings.`,
  upgradeMeetingContextCompanyTitle: `Purchase/request a ${BILLING_ADDONS['ri-addon']} add-on to use Ask Avoma across companies.`,
  upgradeMeetingContextDealTitle: `Purchase/request a ${BILLING_ADDONS['ri-addon']} add-on to use Ask Avoma across deals.`,
  upgradeGlobalContextTitle: `Purchase/request a ${BILLING_ADDONS['ci-addon']} add-on to use global Ask Avoma across all conversations`,
  /**
   * Global-shell gate (no meeting/deal/company in the URL): explains why the composer is blocked
   * instead of only showing the generic global add-on line.
   */
  upgradeGlobalAskAvomaNonEntityShellMessage: `Global Ask Avoma isn’t available on your plan for this page. To use it, open a specific meeting, deal, or company—or upgrade with the ${BILLING_ADDONS['ci-addon']} add-on.`,
  /** Shown when the user already has one meeting in scope but Global Ask Avoma is not licensed (single-meeting limit). */
  singleMeetingContextGlobalLicenseNotice: `Your plan includes Ask Avoma for individual meetings. Purchase/request a ${BILLING_ADDONS['ci-addon']} add-on to add multiple meetings as context in the same Ask Avoma conversation.`,
  addToNotesSearchPlaceholder: 'Search for a category...',
  messageAddedToNotes: 'Added response to notes successfully',
  meetingCitationTooltip: 'View meeting in a new tab',
  emailCitationTooltip: 'View email in a new tab',
  timestampCitationTooltip: 'View transcript in a new tab',
  /** Inline timestamp in citation popover when the cited meeting is the meeting/transcript shell (in-tab seek). */
  timestampCitationTooltipSameShellMeeting:
    'Jump to this moment in the meeting',
  /** Timestamp citation hover when transcript text cannot be loaded off the meeting page. */
  citationTranscriptUnavailableOpenInMeetingAskAvoma:
    'Use the link below to view this citation in context on the meeting detail page and see where it appears in the discussion.',
  citationOpenMeetingAskAvomaLink: 'View chat in meeting',
  speakerAndTranscriptNotAvailable: 'Speaker and transcript not available',
  transcriptNotAvailable: 'Transcript not available',
  contextDropdownTitleLong: 'Ask Avoma about',
  contextDropdownTitleShort: 'Ask about',
  contextMentionMeetings: 'Meetings',
  /** Meetings right pane when Ask Avoma is scoped to the open meeting. */
  contextMentionThisMeetingSectionHeader: 'This meeting',
  /** Add context / @-mention: scoped deal or company row (parallel to “This meeting”). */
  contextMentionThisDeal: 'This deal',
  contextMentionThisCompany: 'This company',
  /** Add context: rows already added to the message, listed first for uncheck. */
  contextMentionSelectedContextSection: 'Selected context',
  /**
   * Composer control beside Add context: primary meeting / deal / company scope (row-group
   * shortcuts). Shown as panel header and trigger label (same copy as `contextDropdownTitleLong`).
   */
  contextMentionLeftRailSwitchContextsHeading: 'Ask Avoma about…',
  /** Scope mini-headers inside the “Ask Avoma about” switch popover (meetings / deals / companies rows). */
  contextMentionLeftRailSwitchPopoverDeals: 'Associated deals',
  contextMentionLeftRailSwitchPopoverCompanies: 'Associated companies',
  /**
   * Suffix for meeting CRM section headers; combined with pluralized “Deal(s)” / “Company(ies)”.
   * e.g. “Deal associated with this meeting”, “Deals associated with this meeting”.
   */
  contextMentionCrmEntitiesAssociatedWithThisMeeting:
    'associated with this meeting',
  contextMentionDeals: 'Deals',
  contextMentionCompanies: 'Companies',
  /**
   * Pre-selected Add context pill when CRM name is still unknown — never use “This deal” / “This company”.
   */
  contextPillUnnamedDeal: 'Deal',
  contextPillUnnamedCompany: 'Company',
  contextPillUnnamedMeeting: 'Meeting',
  contextPillUnnamedPlaylist: 'Playlist',
  contextPillUnnamedTeam: 'Team',
  /** User-message context pill hover card → SPA navigation */
  standaloneContextPillOpenMeeting: 'Open meeting',
  standaloneContextPillOpenDeal: 'Open deal',
  standaloneContextPillOpenCompany: 'Open company',
  standaloneContextPillOpenPlaylist: 'Open playlist',
  contextMentionSearchPlaceholderMeetings: 'Search for a meeting…',
  contextMentionSearchPlaceholderDeals: 'Search for a deal…',
  contextMentionSearchPlaceholderCompanies: 'Search for a company…',
  contextMentionSearchPlaceholderTeams: 'Search for a team…',
  contextMentionSearchPlaceholderMembers: 'Search for a member…',
  contextMentionSearchPlaceholderPlaylists: 'Search for a playlist…',
  contextMentionNoMatches: 'No matching results found',
  /** Composer @ browse (empty query): active category has no default rows to show. */
  contextMentionNoMeetingsFound: 'No meetings found',
  contextMentionNoDealsFound: 'No deals found',
  contextMentionNoCompaniesFound: 'No companies found',
  contextMentionNoTeamsFound: 'No teams found',
  contextMentionNoMembersFound: 'No members found',
  contextMentionNoPlaylistsFound: 'No playlists found',
  contextMentionTypeToSearchHint: 'Start typing to search across all contexts',
  contextMentionOmnisearchLoading: 'Searching…',
  contextMentionDefaultOmnisearchLoading: 'Loading…',
  contextMentionMembers: 'Members',
  /** Secondary line for meeting-derived member rows when the participant is external to the signed-in org’s bucket. */
  contextMentionParticipantExternal: 'External',
  contextMentionMembersAssociatedWithThisMeeting:
    'People associated with this meeting',
  contextMentionMembersAssociatedWithThisCall:
    'People associated with this call',
  contextMentionMembersAssociatedWithThisDeal:
    'People associated with this deal',
  contextMentionMembersAssociatedWithThisCompany:
    'People associated with this company',
  contextMentionTeams: 'Teams',
  /** Team context row subtitle: combined with leading count in code, e.g. “2 members”. */
  contextMentionTeamMemberWordSingular: 'member',
  contextMentionTeamMemberWordPlural: 'members',
  /** Suffix for a team row's subtree total, e.g. “2 members, 6 total”. */
  contextMentionTeamMemberCountTotalWord: 'total',
  /** Fallback display name for a team row that arrives without a name. */
  contextMentionTeamFallbackName: 'Team',
  /** Hover/focus control on a team row to add all of its subteams as context. */
  contextMentionSelectSubteams: 'Select subteams',
  contextMentionDeselectSubteams: 'Deselect subteams',
  contextMentionPlaylists: 'Playlists',
  addContextRefineContext: 'Refine context',
  addContextButtonLabel: 'Add context',
  /** User bubble: standalone context pills without a known categoryKey (grouped menu label). */
  standaloneContextPillsOtherCategoryLabel: 'Context',
  contextMentionClearAll: 'Clear all',
  contextMentionRemoveFromContext: 'Remove from context',
  associatedCompany: 'Associated company',
  associatedCompanies: 'Associated companies',
  associatedDeal: 'Associated deal',
  associatedDeals: 'Associated deals',
  savedPrompts: 'Prompt library',
  attachFiles: 'Attach files',
  attachmentLimitReachedTooltip: 'You can attach up to 5 files per message',
  attachmentRemove: 'Remove attachment',
  attachmentUploadError: 'failed to upload',
  attachmentAlreadyAttached: 'Already attached',
  attachmentDropFiles: 'Attach images, PDFs, and documents',
  attachmentDropSubtitle: 'Drop files here to add them to your message',
  attachmentUploadsRemaining: (count: number): string =>
    `${count} attachment${count === 1 ? '' : 's'} remaining`,
  attachmentTruncationWarning:
    'Large file: content is condensed to fit processing limits, partial context may be dropped.',
  attachmentPreviewOpen: 'Open preview',
  attachmentPreviewLineCount: (count: number): string =>
    `${count.toLocaleString()} line${count === 1 ? '' : 's'}`,
  attachmentPreviewFormattingHint:
    'Formatting may differ from the original file',
  attachmentPreviewRowCap: (count: number): string =>
    `Preview limited to the first ${count.toLocaleString()} rows. Download the file to view the complete dataset.`,
  attachmentPreviewLoading: 'Loading preview…',
  attachmentPreviewError: 'Could not load a preview for this file.',
  attachmentPreviewUnavailable: 'No preview available for this file type.',
  attachmentDownload: 'Download',
  attachmentDownloadError: 'Could not download the file. Please try again.',
  composerActions: 'More actions',
  cancelledRunMessage: 'User stopped this response',
  cancelRunRetryableErrorToast:
    'We could not stop this response. Please try again.',
  composerActionsTooltip: 'Attach files and Web search',
  webSearchLabel: 'Web search',
  webSearchTooltip:
    'When enabled, Avoma can search the web to answer your message',
  webSearchDismissAria: 'Turn off web search',
  webSearchEnableTooltip: 'Enable web search',
  webSearchDisableTooltip: 'Disable web search',
  savePrompt: 'Save prompt',
  saveScheduledPrompt: 'Save as scheduled prompt',
  duplicatePromptTooltip: 'This prompt has already been saved',
  noSavedPrompts: 'No saved prompts',
  untitledPrompt: 'Untitled prompt',
  /** Label used in composer `@[display](id)` when the mention display sanitizes to an empty string. */
  copilotContextMentionUnknownDisplay: 'Unknown',
  deletePromptTooltip: 'Delete prompt',
  usePromptTooltip: 'Use prompt',
  editPromptTooltip: 'Edit prompt',
  editScheduledPromptTooltip: 'Edit scheduled prompt',
  createScheduleTooltip: 'Create schedule',
  editScheduleTooltip: 'Edit schedule',
  /** History sidebar / scheduled report actions (removes the schedule and its reports). */
  deleteScheduleTooltip: 'Remove schedule',
  savedPromptSuccessToast: 'Saved prompt successfully',
  savedPromptErrorToast: 'Failed to save prompt',
  savedPromptDeleteSuccessToast: 'Deleted prompt successfully',
  savedPromptDeleteErrorToast: 'Failed to delete prompt',
  searchSavedPromptsPlaceholder: 'Search for a saved prompt...',
  /** Unified Prompt Library search: prompt titles and body only (not collections). */
  searchSavedPromptsPlaceholderUnified: 'Search for a prompt...',
  searchSavedPromptsPlaceholderPrivate: 'Search for your prompt...',
  searchSavedPromptsPlaceholderOrganization: 'Search for an org prompt...',
  searchSavedPromptsPlaceholderDefault: 'Search for a default prompt...',
  searchSavedPromptsPlaceholderCollections: 'Search for a collection...',
  searchSavedPromptsPlaceholderScheduledForYou:
    'Search for a scheduled prompt...',
  loadMoreChats: 'Load more chats',
  retryLoadMoreChats: 'Retry',
  loadMoreChatsError: 'Could not load more chats',
  noSavedPromptsFound: 'No matching prompts found',
  noCollectionsFound: 'No collections',
  noMatchingCollectionsFound: 'No matching collections found',
  searchResultsTitle: 'Search results',
  clearSearchResults: 'Clear search',
  searchResultsGroupMyPrompts: 'My prompts',
  searchResultsGroupTeammates: "Teammates' prompts",
  searchResultsGroupDefault: 'Default prompts',
  savedPromptsEmpty: 'No prompts',
  collectionPrivatePromptsInfo:
    'Collection may contain private prompts not visible to you',
  noMembersFound: 'No members found',
  noSortOptionsFound: 'No sort options found',
  sortBy: 'Sort by',
  filterByVisibility: 'Filter by privacy',
  visibilityFilterAll: 'All',
  visibilityFilterPrivate: 'Only me',
  visibilityFilterOrg: 'Org-level',
  askAvomaPlaceholder:
    'Ask Avoma about anything. Use ‘@’ to refer meetings, deals and companies.',
  /** Composer when @ context mentions are disabled (e.g. single-meeting CI, deal without global). */
  askAvomaPlaceholderWithoutContextMentions: 'Ask Avoma about...',
  askAvomaSettingsTitle: 'Customize Ask Avoma',
  responsePreferencesLabel: 'How should Ask Avoma respond?',
  responsePreferencesHelperText:
    'Set a preferred tone, level of detail, and format for all responses',
  responsePreferencesPlaceholder:
    'e.g. use a concise, executive-friendly tone with bullet points and clear next steps',
  customizeResponseSuccessToast: 'Response customization updated successfully',
  customizeResponseErrorToast: 'Failed to update response customization',
  askAvomaDisclaimer: 'Ask Avoma is AI and can make mistakes.',
  includeTimestampsOnCopy: 'Include meeting timestamps when copying responses?',
  viewAllMessages: 'View all messages',
  showLess: 'Hide older messages',
  newChat: 'New chat about...',
  /** Single-row CTA when the page has one clear context target (not the short `newChat` dropdown label). */
  newChatAboutPrefix: 'New chat about',
  /** History sidebar: create a new session with global (non-URL) context from a page that also offers meeting/deal/company. */
  newChatMenuGlobal: 'Anything',
  openHistory: 'Open history',
  closeHistory: 'Close history',
  searchHistoryPlaceholder: 'Search chats...',
  noChatHistoryFound: 'No chat history',
  noSearchResultsFound: 'No matching chats found',
  noScheduledPromptsFound: 'No scheduled prompts found',
  noSearchResultsFoundScheduledPrompts: 'No matching schedules found',
  deleteChatTitle: 'Delete chat?',
  deleteScheduledPromptTitle: 'Delete scheduled prompt?',
  /** Report list / history / manage: removing automation only (PATCH), not DELETE prompt. */
  removeScheduledPromptScheduleTitle: 'Remove schedule?',
  removeScheduledPromptScheduleLead:
    'Are you sure you want to remove the schedule for',
  removeScheduledPromptScheduleTrailing:
    '? Automated runs will stop. Past reports stay available, and the saved prompt is not deleted.',
  removeScheduledPromptScheduleConfirm: 'Remove schedule',
  removeScheduledPromptScheduleSuccessToast: 'Schedule removed',
  deleteOrgPromptTitle: 'Delete organization prompt?',
  deleteOrgPromptButton: 'Delete prompt',
  deleteOrgPromptAffectedMembers:
    'Schedules will be deleted for the following members:',
  deleteReportTooltip: 'Delete report',
  deleteReportTitle: 'Delete report?',
  deleteChatSuccessToast: 'Chat deleted successfully',
  deleteChatErrorToast: 'Failed to delete chat',
  updateChatNameSuccessToast: 'Chat renamed successfully',
  updateChatNameErrorToast: 'Failed to rename chat',
  pinChat: 'Pin chat',
  unpinChat: 'Unpin chat',
  pinChatSuccessToast: 'Chat pinned',
  unpinChatSuccessToast: 'Chat unpinned',
  pinChatErrorToast: 'Failed to pin chat',
  unpinChatErrorToast: 'Failed to unpin chat',
  pinChatLimitToast:
    'You can only pin up to 10 chats. Unpin one to pin a new chat.',
  scrollToBottom: 'Scroll to bottom',
  scrollToTop: 'Scroll to top',
  scrollToNextMessage: 'Scroll to next message',
  scrollToPreviousMessage: 'Scroll to previous message',
  isPlayingDisabledTooltip: 'Pause the video to view the citation',
  pauseScheduledPromptSuccessToast: 'Scheduled prompt paused',
  schedulePausedBadgeLabel: 'Schedule paused',
  resumeScheduledPromptSuccessToast: 'Scheduled prompt resumed',
  markAllScheduledPromptReportsAsReadSuccessToast:
    'All scheduled prompt reports marked as read',
  updateScheduledPromptSuccessToast: 'Scheduled prompt updated successfully',
  updateScheduledPromptErrorToast: 'Failed to update scheduled prompt',
  deleteScheduledPromptSuccessToast: 'Scheduled prompt deleted successfully',
  deleteScheduledPromptErrorToast: 'Failed to delete scheduled prompt',
  createScheduledPromptSuccessToast: 'Scheduled prompt created successfully',
  createScheduledPromptErrorToast: 'Failed to create scheduled prompt',
  createMeetingEndAutomationErrorToast:
    'Your prompt was saved, but the meeting trigger could not be enabled. Please try again or contact support.',
  updateMeetingEndAutomationErrorToast:
    'Your prompt was updated, but the meeting trigger could not be saved. Please try again or contact support.',
  deleteMeetingEndAutomationErrorToast:
    'Your prompt was updated, but the meeting trigger could not be removed. Please try again or contact support.',
  deletePromptAndSchedule: 'Delete prompt & schedule',
  generatePromptNameSuccessToast: 'Name generated successfully',
  generatePromptNameErrorToast: 'Failed to generate prompt name',
  refinePromptSuccessToast: 'Prompt refined successfully',
  refinePromptErrorToast: 'Failed to refine prompt',
  new: 'New',
  newPrompt: 'New prompt',
  newScheduledPrompt: 'Schedule a new prompt',
  newScheduledPromptDisabledTooltip:
    "You've reached the limit of 10 active scheduled prompts. Pause or delete an existing schedule to create another.",
  updateScheduledPromptFrequencyDisabledTooltip:
    "You've reached the limit of 10 active scheduled prompts. Pause or delete an existing schedule to enable another.",
  resumeScheduledPromptDisabledTooltip:
    "You've reached the limit of 10 active scheduled prompts. Pause or delete an existing prompt to resume the schedule for this prompt.",
  /** Shown while meeting-end automation GET is in flight so pause/resume is not mislabeled. */
  scheduledPromptMeetingEndStatusLoadingTooltip: 'Loading schedule status…',
  manageScheduledPrompts: 'Manage scheduled prompts',
  scheduled: 'Scheduled',
  scheduledViaMeetingRulesBadge: 'Scheduled via meeting rules',
  chats: 'All Chats',
  /** History sidebar section headers for chats scoped to the current entity shell. */
  historySidebarPinnedSection: 'Pinned',
  historySidebarThisMeetingSection: 'Chats for this meeting',
  historySidebarThisPlaylistSection: 'Chats for this playlist',
  historySidebarThisDealSection: 'Chats for this deal',
  historySidebarThisCompanySection: 'Chats for this company',
  historySidebarNoRelatedChats: 'No chats yet',
  backToScheduledPrompt: 'Back to scheduled prompt',
  updatePromptInfo:
    'Any changes made to this prompt will take effect on its next scheduled run.',
  updateScheduleInfo:
    'Any changes made to this frequency will take effect on its next scheduled run.',
  updateMeetingEndTriggerInfo:
    'Any changes made to this automation will apply to meetings that end after you save.',
  scheduledPromptScheduleKindSectionTitle:
    'How should this prompt be triggered?',
  scheduledPromptScheduleKindFrequencyTitle: 'On a regular schedule',
  scheduledPromptScheduleKindFrequencyDescription: 'Runs at a recurring time',
  scheduledPromptScheduleKindMeetingEndTitle: 'After each meeting ends',
  scheduledPromptScheduleKindMeetingEndDescription:
    'Runs automatically after each meeting ends instead of on a recurring schedule. Add filter rules to choose which meetings qualify—it runs only when every rule matches.',
  meetingEndTriggerRulesTitle: 'Meeting conditions',
  meetingEndTriggerRulesDescription:
    'The prompt runs after a meeting ends when all of the following are true.',
  /** Heading inside shared Automations `AutomationGroupFilters` (matches Automations “Rules”). */
  meetingEndTriggerAutomationRulesHeading: 'Rules',
  meetingEndTriggerRulesLoadError:
    'We could not load meeting trigger fields. Check your connection and try again.',
  loadMeetingEndAutomationError:
    'We could not load meeting trigger rules for this schedule. Check your connection and try again.',
  meetingEndTriggerRulesNoneAvailable:
    'No filter fields are available for this trigger. If this persists, contact support.',
  meetingEndTriggerRulesEmptyInfo:
    'Add at least one rule group to use this trigger type.',
  meetingEndTriggerRulesAnd: 'AND',
  /** Accordion header for each meeting-end condition group (`{number}` is 1-based). */
  meetingEndRuleGroupTitle: 'Rule group {number}',
  /** Label for the primary “add OR group” control; shown with the leading + icon like scorecards “Rule”. */
  meetingEndAddNewRuleGroupLabel: 'Rule group',
  meetingEndTriggerRuleFieldLabel: 'Field',
  meetingEndTriggerRuleOperatorLabel: 'Operator',
  meetingEndTriggerRuleValueLabel: 'Value',
  meetingEndTriggerPickFieldFirst: 'Select a field to edit its value.',
  meetingEndTriggerRuleRemove: 'Remove condition',
  meetingEndTriggerAddConditionLabel: 'Add condition',
  meetingEndTriggerAddConditionPlaceholder: 'Choose a field to filter on',
  meetingEndTriggerNoMoreConditions:
    'All available fields have been added for this trigger.',
  scheduledPromptScheduleStatusActiveMeetingEnd:
    'This prompt runs automatically after your meetings complete & match rules',
  scheduledPromptPausedInfo:
    'This schedule is currently paused. Your frequency and notification settings are preserved and will be honored when you resume the schedule.',
  scheduledPromptNamePlaceholder: 'e.g. Engagement analysis',
  schedulePrompt: 'Schedule prompt',
  newScheduledPromptInfo:
    'Automatically run prompts on a recurring schedule or after meetings that match your rules.',
  scheduledUserInfo:
    'This prompt will be scheduled for you only, not your organization',
  editOrgPromptInfo:
    'You are currently editing an organization-wide prompt. All scheduled uses will reflect any updates to this prompt, but existing schedules will remain unchanged.',
  downgradingOrgPromptToPrivateInfo:
    'If you save this prompt as private, your organization will no longer have access, and any schedules for other members will be removed.',
  createOrgPromptWarning:
    "After saving this as an organization prompt, it can't be made private later.",
  downgradeOrgPromptToPrivateTitle: 'Make this prompt private?',
  downgradeOrgPromptToPrivateMessage:
    "Saving this prompt as private will remove organization access and delete all schedules for other members. The prompt author's schedule will remain unaffected.",
  downgradeOrgPromptToPrivateAffectedMembers:
    'Schedules will be deleted for the following members:',
  saveAsPrivate: 'Save as private',
  discardChangesTitle: 'Discard unsaved changes?',
  discardChangesMessage:
    'You have unsaved changes. If you proceed without saving, your changes will be lost and you will not be able to recover them.',
  continueEditing: 'Continue editing',
  discardChanges: 'Discard changes',
  saveChanges: 'Save changes',
  create: 'Create',
  cancel: 'Cancel',
  orgSavedPrompts: 'Org Saved Prompts',
  upgradeOrgSavedPromptsTitle,
  creditFeaturesMayPause:
    'Features that use credits will stop when credits are exhausted',
  creditFeaturesPaused: 'Features that use credits have stopped',
  promptLibraryGateTitle: 'Prompt library requires a paid license',
  promptLibraryGateSubtitles: [
    'Create and manage custom AI prompts for Ask Avoma',
    'Save frequently used prompts for quick access',
    'Share prompts across your organization'
  ],
  promptLibraryBreadcrumbText: 'Prompt library',
  newCollection: 'New collection',
  defaultPromptCollectionLocked:
    'Default prompts cannot have their collections changed',
  addToCollectionHelperDescription:
    'Organize your prompts by adding them to a collection, including default collections, for easier access. You can remove a prompt from a collection the same way.',
  scheduledForYou: 'Scheduled for you',
  promptCollectionVisibilityInfo:
    'Collections are visible to everyone in your organization and can include both private and organization-level prompts. Any private prompts included in a collection remain visible only to the user who created them.',
  collectionName: 'Name',
  collectionNamePlaceholder: 'e.g. Onboarding Collection',
  collectionDescription: 'Description',
  collectionDescriptionPlaceholder: 'e.g. Collection for onboarding process',
  editCollection: 'Edit collection',
  createNewCollection: 'Create a new collection',
  collectionCreatedSuccessToast: 'Collection created successfully',
  collectionCreatedErrorToast: 'Failed to create collection. Please try again.',
  collectionUpdatedSuccessToast: 'Collection updated successfully',
  collectionUpdatedErrorToast: 'Failed to update collection. Please try again.',
  collectionDeletedSuccessToast: 'Collection deleted successfully',
  collectionDeletedErrorToast: 'Failed to delete collection. Please try again.',
  creditBalance: 'Credit balance',
  creditBalanceSubtitle:
    'Scheduling prompts consumes credits from your org pool',
  creditsRemainingMessage: 'left for your organization',
  creditsResets: 'Resets on',
  scheduledPromptsOrgUsageCardTitle: 'Organization usage',
  scheduledPromptsOrgUsageScheduledPromptsUsed: 'Scheduled Prompts Used',
  scheduledPromptsOrgUsageRemaining: 'remaining',
  scheduledPromptsOrgLimitWarningTitle: 'Approaching organization limit.',
  scheduledPromptsOrgLimitWarningSubtext:
    'New scheduled prompts cannot be created once the limit is reached.',
  scheduledPromptsOrgLimitReachedTitle: 'Organization limit reached.',
  scheduledPromptsOrgLimitReachedSubtext:
    'No new scheduled prompts can be enabled.',
  scheduledPromptsOrgFreePreviewBadge: 'Free Preview',
  scheduledPromptsOrgFreePreviewDisclaimer:
    'Scheduled Prompts is currently in Free Preview. Pricing and limits subject to change.',
  scheduledPromptCreditBannerCriticalTitle: 'Low credits warning.',
  scheduledPromptCreditBannerCriticalPrefix: 'Only',
  scheduledPromptCreditBannerCriticalSuffix:
    'left. Scheduled prompts will stop running when credits run out.',
  scheduledPromptCreditBannerCriticalSuffixMember:
    'left. Scheduled prompts will stop running when credits run out. Contact your admin if you need more credits.',
  scheduledPromptPausedCreditsExhausted: 'Credits exhausted',
  scheduledPromptPausedCreditsExhaustedMessage:
    'This prompt will not run because credits have been exhausted.',
  scheduledPromptPausedCreditsExhaustedContact:
    'Contact your admin to top up credits.',
  scheduledPromptPausedCreditsExhaustedBuyMoreCredits: 'Top up credits',
  creditsRemainingMessageUser: 'left',
  userCreditLimitReachedStatus: 'Contact your admin to increase your limit',
  userCreditLimitBannerTitle: 'User credit limit reached.',
  userCreditLimitBannerSuffix:
    'Your scheduled prompts are paused. Contact your admin to increase your monthly limit.',
  scheduledPromptPausedUserLimitTitle: 'Credit limit reached',
  scheduledPromptPausedUserLimitMessage:
    'This prompt won’t run because you’ve reached your monthly credit limit.',
  scheduledPromptPausedUserLimitContact:
    'Contact your admin to increase your limit.',
  scheduledPromptPausedUserLimitManage: 'Manage credit limits'
} as const;
